import { usePlanEffectiveDate, useRfpId } from "../contexts";
import {
  DOWNLOAD_ERROR_MESSAGE,
  PDF_PREVIEW_ONLY_ERROR_MESSAGE,
  REMOVE_ERROR_MESSAGE,
  RENAME_ERROR_MESSAGE,
  VIEW_ERROR_MESSAGE,
  OPEN_ERROR_MESSAGE,
} from "../constants";
import { downloadBinaryFile, isPdf } from "../utils";

import { useConfirmationDialog } from "./useConfirmationDialog";
import { useMultipleLocModal } from "./useMultipleLocModal";
import { useRenameDocumentModal } from "./useRenameDocumentModal";
import {
  useDownloadRfpFile,
  useProcessDocumentMutation,
  useRemoveDocumentMutation,
  useRemoveDocumentCoverageMutation,
  useRenameDocumentMutation,
} from "./useDocumentLibraryApi";
import { useError } from "./useError";

import type { DocumentCardProps } from "../types";

export type DocumentActionModalType = "processAI" | "remove" | "removeMultiLoc";

type ActionConfig = {
  getTitle: (bulk: boolean) => string;
  getDescription: (bulk: boolean) => string;
  confirmButtonText: string;
  requiresConfirmation: boolean;
  additionalMessage?: string;
};

const ACTION_CONFIGS: Record<
  Exclude<DocumentActionModalType, "removeMultiLoc">,
  ActionConfig
> = {
  processAI: {
    getTitle: () => "Process with AI?",
    getDescription: (bulk) =>
      bulk
        ? "Are you sure you want to process these documents with AI? This will replace any data currently in the table. This action cannot be undone."
        : "Are you sure you want to process this document with AI? This will replace any data currently in the table. This action cannot be undone.",
    confirmButtonText: "Confirm",
    requiresConfirmation: true,
  },
  remove: {
    getTitle: (bulk) => (bulk ? "Remove Documents?" : "Remove Document?"),
    getDescription: (bulk) =>
      bulk
        ? "Are you sure you want to remove these documents? This will remove any plan data currently in the table for all lines of coverage associated with these documents. They will still be available in SharePoint."
        : "Are you sure you want to remove this document? This will remove any plan data currently in the table for all lines of coverage associated with this document. This will still be available in SharePoint.",
    confirmButtonText: "Confirm",
    requiresConfirmation: true,
  },
};

export const useDocumentActions = (
  selectedIds: Set<string>,
  onComplete?: () => void,
  documentsById?: Record<string, DocumentCardProps>,
): {
  actions: {
    viewDocument: () => void;
    downloadDocument: () => void;
    renameDocument: () => void;
    processWithAI: () => void;
    removeFile: () => void;
  };
} => {
  const { openModal } = useMultipleLocModal();
  const { openModal: openRenameModal } = useRenameDocumentModal();
  const isBulkMode = selectedIds.size > 1;
  const { openDialog } = useConfirmationDialog();
  const { setError, clearError } = useError();
  const processDocumentMutation = useProcessDocumentMutation();
  const removeDocumentMutation = useRemoveDocumentMutation();
  const removeDocumentCoverageMutation = useRemoveDocumentCoverageMutation();
  const renameDocumentMutation = useRenameDocumentMutation();
  const rfpId = useRfpId();
  const planEffectiveDate = usePlanEffectiveDate();
  const { downloadFile } = useDownloadRfpFile();

  const downloadDocumentFile = async (
    document: DocumentCardProps,
  ): Promise<void> => {
    if (!document.metadata.downloadUrl) {
      throw new Error(`Document ${document.id} does not have a download URL`);
    }

    const binaryFile = await downloadFile(document.metadata.downloadUrl);

    downloadBinaryFile(binaryFile, document.fileName);
  };

  // Default callback implementations
  const defaultCallbacks = {
    viewDocument: (): void => {
      if (isBulkMode) {
        window.alert(
          `Opening document viewers for: ${Array.from(selectedIds).join(", ")}`,
        );
        return;
      }

      const documentId = Array.from(selectedIds)[0];
      const foundDocument = documentsById?.[documentId];

      if (!foundDocument) {
        setError("docs", OPEN_ERROR_MESSAGE);
        return;
      }

      if (!foundDocument.metadata.downloadUrl) {
        setError("docs", OPEN_ERROR_MESSAGE);
        return;
      }

      if (!isPdf(foundDocument.metadata.downloadUrl)) {
        setError("docs", PDF_PREVIEW_ONLY_ERROR_MESSAGE);
        return;
      }

      void (async () => {
        try {
          const viewerWindow = window.open("", "_blank");
          if (!viewerWindow) {
            setError("docs", VIEW_ERROR_MESSAGE);
            return;
          }

          viewerWindow.opener = null;

          const binaryFile = await downloadFile(
            foundDocument.metadata.downloadUrl,
          );

          const blob = new Blob([binaryFile], {
            type: "application/pdf",
          });

          const blobUrl = URL.createObjectURL(blob);

          viewerWindow.location.href = blobUrl;

          setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

          clearError("docs");
        } catch {
          setError("docs", VIEW_ERROR_MESSAGE);
        }
      })();
    },
    downloadDocument: async (): Promise<void> => {
      const documentId = Array.from(selectedIds)[0];
      const foundDocument = documentsById?.[documentId];
      if (!foundDocument) {
        setError("docs", DOWNLOAD_ERROR_MESSAGE);
        return;
      }

      try {
        await downloadDocumentFile(foundDocument);
        clearError("docs");
      } catch {
        setError("docs", DOWNLOAD_ERROR_MESSAGE);
      }
    },
    processWithAI: (): void => {
      const documentIds = Array.from(selectedIds);

      if (documentIds.length === 0) {
        return;
      }

      void (async () => {
        await Promise.allSettled(
          documentIds.map((documentId) =>
            processDocumentMutation.mutateAsync({
              rfpId,
              documentId,
              planEffectiveDate,
            }),
          ),
        );
      })();
    },
    removeFile: (): void => {
      if (isBulkMode) {
        return;
      }

      const documentId = Array.from(selectedIds)[0];
      if (!documentId) {
        return;
      }

      void (async () => {
        try {
          await removeDocumentMutation.mutateAsync({ rfpId, documentId });
          clearError("docs");
        } catch {
          setError("docs", REMOVE_ERROR_MESSAGE);
        }
      })();
    },
    renameDocumentFile: async (
      documentId: string,
      newFileName: string,
    ): Promise<void> => {
      try {
        await renameDocumentMutation.mutateAsync({
          rfpId,
          documentId,
          newDocumentName: newFileName,
        });
        clearError("docs");
      } catch {
        setError("docs", RENAME_ERROR_MESSAGE);
      }
    },
    removeMultiLocFile: (selectedLocs: string[]): void => {
      if (isBulkMode) {
        return;
      }

      const documentId = Array.from(selectedIds)[0];
      if (!documentId || selectedLocs.length === 0) {
        return;
      }

      void (async () => {
        try {
          await removeDocumentCoverageMutation.mutateAsync({
            rfpId,
            documentId,
            coverages: selectedLocs.map((coverageType) => ({ coverageType })),
          });
          clearError("docs");
        } catch {
          setError("docs", REMOVE_ERROR_MESSAGE);
        }
      })();
    },
  };

  const executeAction = (actionType: DocumentActionModalType): void => {
    if (actionType === "removeMultiLoc") {
      const documentId = Array.from(selectedIds)[0];
      const doc = documentsById?.[documentId];
      openModal(doc?.multipleLoc ?? [], (selectedLocs) => {
        defaultCallbacks.removeMultiLocFile(selectedLocs);
        onComplete?.();
      });
      return;
    }

    const config = ACTION_CONFIGS[actionType];

    // Map actionType to the correct callback
    const getDefaultCallback = (
      type: DocumentActionModalType,
    ): (() => void) => {
      switch (type) {
        case "processAI":
          return () => {
            defaultCallbacks.processWithAI();
            onComplete?.();
          };
        case "remove":
          return () => {
            defaultCallbacks.removeFile();
            onComplete?.();
          };
        default:
          return (): void => {};
      }
    };

    const callback = getDefaultCallback(actionType);

    if (!config.requiresConfirmation) {
      callback();
      return;
    }

    const getDialogData = (): {
      title: string;
      message: string;
      confirmBtnMsg: string;
      onConfirm: () => void;
    } => ({
      title: config.getTitle(isBulkMode),
      message: config.getDescription(isBulkMode),
      confirmBtnMsg: config.confirmButtonText,
      onConfirm: callback,
    });

    const dialogData = getDialogData();
    openDialog(dialogData);
  };

  return {
    actions: {
      viewDocument: (): void => {
        defaultCallbacks.viewDocument();
        onComplete?.();
      },
      downloadDocument: (): void => {
        void defaultCallbacks.downloadDocument();
        onComplete?.();
      },
      renameDocument: (): void => {
        if (isBulkMode) {
          return;
        }

        const documentId = Array.from(selectedIds)[0];
        const foundDocument = documentsById?.[documentId];

        if (!foundDocument) {
          return;
        }

        openRenameModal(foundDocument.fileName, (newFileName) =>
          defaultCallbacks.renameDocumentFile(documentId, newFileName),
        );
        onComplete?.();
      },
      processWithAI: (): void => executeAction("processAI"),
      removeFile: (): void => {
        const documentId = Array.from(selectedIds)[0];
        const doc = !isBulkMode ? documentsById?.[documentId] : undefined;
        const removesSelectedLocs = Boolean(doc?.multipleLoc);
        executeAction(removesSelectedLocs ? "removeMultiLoc" : "remove");
      },
    },
  };
};
